import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { LAST_LEVEL_ID, CH2_START_LEVEL_ID, isChapter2Cleared } from '../../config/levels';
import { shopPages } from '../../config/shopPages';
import { BALANCE } from '../../config/balance';
import { SHOP_PRICES, type ShopTowerType } from '../../config/unlocks';
import { TOWER_NAMES, TOWER_TUTORIALS } from '../../config/displayNames';
import { ProgressService } from '../../services/progressService';
import { RankService } from '../../services/rankService';
import { enqueueAchievementToast, sweepAndToastAchievements } from '../../ui/achievementToast';
import { saveStorage } from '../saveStorage';
import { recordStat } from '../quests';
import { towerSpecial, towerStatLine } from '../towerInfo';
import { spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';
const ROW_W = 880;
const ROW_H = 130;
/** Row centres — fixed for the e2e click targets (BUY sits at x=975). */
const ROW_YS = [210, 360, 510];

/**
 * ShopScene — spend persistent Hambi coins.
 *
 * It sells the six PERMANENT towers (see `SHOP_SALE_TOWERS`): the Cheese
 * Bladesmen, BBQ Bomber and Wise Hambi on page 1, and the Chapter-2 trio —
 * General Sniper, Dark Hambi, Ninja Hambi — on the gated page 2. Each is a
 * one-time purchase; once owned it is available in every episode (it still
 * costs in-level cash to build).
 *
 * It does NOT sell Boxer, Gunner, Fire Mage, Aqua Sorcerer or Phantom. Those
 * five come out of a Normal Takeout Bag in the Restaurant, reached from the
 * pill below the header.
 *
 * Reached from MenuScene's SHOP button; BACK returns there.
 */
export class ShopScene extends Phaser.Scene {
  private progress!: ProgressService;
  private balanceText!: Phaser.GameObjects.Text;
  /** Current rows (torn down and rebuilt after a purchase). */
  private rowsContainer?: Phaser.GameObjects.Container;
  /** Transient "not enough coins" hint. */
  private hint?: Phaser.GameObjects.Text;
  /** True while the confirm dialog is up — row buttons ignore clicks. */
  private confirmOpen = false;
  /** Current shop page index. */
  private page = 0;
  /** Pages of shop towers (page 2+ present once Chapter 2 is unlocked, or the `shop2` dev flag is granted). */
  private pagesData: ShopTowerType[][] = [];
  private leftArrow?: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private rightArrow?: { box: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
  private pageLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'ShopScene' });
  }

  create(): void {
    const cx = 640;
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID);
    this.rowsContainer = undefined;
    this.hint = undefined;
    this.confirmOpen = false;
    this.page = 0;
    // Page 2 (Chapter-2 towers) opens the moment Chapter 2 becomes playable —
    // i.e. its first level is unlocked after beating Chapter 1 — not on clearing
    // it. A dev/admin can also grant it early with the `shop2` dev flag.
    this.pagesData = shopPages(
      this.progress.isUnlocked(CH2_START_LEVEL_ID) || this.progress.hasFlag('shop2'),
    );
    this.leftArrow = undefined;
    this.rightArrow = undefined;
    this.pageLabel = undefined;

    // Kitchen backdrop (real bg-shop.png when generated, procedural placeholder
    // otherwise), dimmed so the gold text and row panels stay readable over it.
    this.add.image(640, 360, 'bg-shop').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.45);

    // ── Header ──────────────────────────────────────────────────────────────
    this.add
      .text(cx, 48, 'SHOP', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(cx, 84, 'ONE-TIME PURCHASES · YOURS IN EVERY EPISODE', {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    // Back button — same styling/position as LevelSelectScene's.
    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('MenuScene'));

    // Coin balance — same spot as LevelSelectScene's top bar.
    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.balanceText = this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // ── Restaurant + Evolve + Equip entry points ─────────────────────────────
    // Gold pills below the header, above the first row (y=210). RESTAURANT sits
    // to the LEFT of EVOLVE, which is shown only once ALL of Chapter 2 is
    // cleared (or the `evolve` dev flag is granted early). With no Evolve pill
    // the other two straddle the centre.
    if (isChapter2Cleared(this.progress) || this.progress.hasFlag('evolve')) {
      this.makePillButton(cx - 280, 120, '🍔  RESTAURANT', 'shop-restaurant', () =>
        this.scene.start('RestaurantScene'),
      );
      this.makePillButton(cx, 120, '🧬  EVOLVE', 'shop-evolve', () =>
        this.scene.start('EvolveScene'),
      );
      this.makePillButton(cx + 280, 120, '⚙  EQUIP LOADOUT', 'shop-equip', () =>
        this.scene.start('EquipScene'),
      );
    } else {
      this.makePillButton(cx - 140, 120, '🍔  RESTAURANT', 'shop-restaurant', () =>
        this.scene.start('RestaurantScene'),
      );
      this.makePillButton(cx + 140, 120, '⚙  EQUIP LOADOUT', 'shop-equip', () =>
        this.scene.start('EquipScene'),
      );
    }

    // ── Rows + footer ────────────────────────────────────────────────────────
    this.renderRows();
    this.renderPager();
    this.add
      .text(
        cx,
        640,
        'Boxer, Gunner, Fire Mage, Aqua Sorcerer and Phantom come from the Restaurant.',
        {
          fontSize: '15px',
          color: '#b8c4c6',
          fontStyle: 'italic',
        },
      )
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** Gold pill button (the Restaurant/Equip/Evolve entry-point style). */
  private makePillButton(x: number, y: number, text: string, name: string, onClick: () => void) {
    const box = this.add
      .rectangle(x, y, 260, 44, 0x15131f, 0.9)
      .setStrokeStyle(2, GOLD, 0.9)
      .setInteractive({ useHandCursor: true })
      .setDepth(6)
      .setName(name);
    const label = this.add
      .text(x, y, text, { fontSize: '20px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(7);
    box.on('pointerover', () => {
      box.setFillStyle(0x241f33, 0.95);
      label.setColor('#fff3b0');
    });
    box.on('pointerout', () => {
      box.setFillStyle(0x15131f, 0.9);
      label.setColor(GOLD_HEX);
    });
    box.on('pointerup', () => {
      if (this.confirmOpen) return;
      onClick();
    });
  }

  /** (Re)draw the current page's tower rows to match balance + ownership. */
  private renderRows(): void {
    this.rowsContainer?.destroy();
    const container = this.add.container(0, 0).setDepth(1);
    this.rowsContainer = container;
    const towers = this.pagesData[this.page] ?? [];
    towers.forEach((type, i) => this.makeRow(640, ROW_YS[i], type, container));
  }

  /** Build the page arrows + indicator (only when there is more than one page). */
  private renderPager(): void {
    if (this.pagesData.length <= 1) return;
    const y = 600;
    const mkArrow = (x: number, glyph: string, dir: -1 | 1) => {
      const box = this.add
        .rectangle(x, y, 60, 48, 0x15131f, 0.85)
        .setStrokeStyle(1, GOLD, 0.5)
        .setInteractive({ useHandCursor: true })
        .setDepth(4);
      const label = this.add
        .text(x, y, glyph, { fontSize: '26px', color: GOLD_HEX, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(5);
      box.on('pointerup', () => {
        if (this.confirmOpen) return;
        const next = Math.min(Math.max(this.page + dir, 0), this.pagesData.length - 1);
        if (next === this.page) return;
        this.page = next;
        this.renderRows();
        this.updatePager();
      });
      return { box, label };
    };
    this.leftArrow = mkArrow(560, '◀', -1);
    this.rightArrow = mkArrow(720, '▶', 1);
    this.pageLabel = this.add
      .text(640, y, '', { fontSize: '18px', color: '#cfe0bd', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(5);
    this.updatePager();
  }

  /** Sync the page indicator + dim the arrow that can't move any further. */
  private updatePager(): void {
    if (!this.pageLabel) return;
    this.pageLabel.setText(`${this.page + 1} / ${this.pagesData.length}`);
    const atFirst = this.page === 0;
    const atLast = this.page === this.pagesData.length - 1;
    this.leftArrow?.label.setAlpha(atFirst ? 0.3 : 1);
    this.leftArrow?.box.setAlpha(atFirst ? 0.4 : 0.85);
    this.rightArrow?.label.setAlpha(atLast ? 0.3 : 1);
    this.rightArrow?.box.setAlpha(atLast ? 0.4 : 0.85);
  }

  /** One shop row: art, name, stats, blurb, price, and BUY / OWNED state. */
  private makeRow(
    cx: number,
    y: number,
    type: ShopTowerType,
    container: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const right = cx + ROW_W / 2;
    const owned = this.progress.hasTower(type);
    const price = SHOP_PRICES[type];
    const affordable = this.progress.coins() >= price;
    const spec = BALANCE.towers[type].tier1;

    // Panel — gold-stroked; green once owned.
    const panel = this.add.graphics();
    panel.fillStyle(0x15131f, 0.85);
    panel.fillRoundedRect(left, y - ROW_H / 2, ROW_W, ROW_H, 12);
    panel.lineStyle(owned ? 2 : 1, owned ? 0x4caf50 : GOLD, owned ? 0.9 : 0.5);
    panel.strokeRoundedRect(left, y - ROW_H / 2, ROW_W, ROW_H, 12);
    container.add(panel);

    // Art (tier-1), name, stat line, blurb — mirrors the Tower Guide rows.
    const artKey = this.textures.exists(`tower-${type}-1`) ? `tower-${type}-1` : 'tower-patty-1';
    container.add(this.add.image(left + 70, y, artKey).setDisplaySize(90, 90));
    container.add(
      this.add
        .text(left + 130, y - 40, TOWER_NAMES[type], {
          fontSize: '21px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );
    // Text column ends where the price coin begins (x=830, i.e. 500px from the
    // column's left edge at 330) — long stat lines scale down instead of
    // running under the coin, and the blurb wraps short of it.
    const TEXT_MAX_W = 470;
    const special = towerSpecial(spec, type);
    const statLine = this.add
      .text(left + 130, y - 12, towerStatLine(spec) + (special ? `  ·  ${special}` : ''), {
        fontSize: '14px',
        color: '#9fd8ff',
      })
      .setOrigin(0, 0.5);
    if (statLine.width > TEXT_MAX_W) statLine.setScale(TEXT_MAX_W / statLine.width);
    container.add(statLine);
    container.add(
      this.add
        .text(left + 130, y + 18, TOWER_TUTORIALS[type], {
          fontSize: '13px',
          color: '#9aa6ad',
          wordWrap: { width: TEXT_MAX_W },
        })
        .setOrigin(0, 0.5),
    );

    if (owned) {
      container.add(
        this.add
          .text(right - 105, y, 'OWNED', { fontSize: '24px', color: '#4caf50', fontStyle: 'bold' })
          .setOrigin(0.5),
      );
      return;
    }

    // Price tag (static coin — the spinning one in the header is enough motion).
    container.add(this.add.image(right - 250, y, 'coin').setDisplaySize(30, 30));
    container.add(
      this.add
        .text(right - 228, y, `${price}`, {
          fontSize: '22px',
          color: affordable ? GOLD_HEX : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );

    // BUY button — gold when affordable, dimmed (but still tappable, so it can
    // explain itself) when not.
    const btn = this.add
      .rectangle(right - 105, y, 150, 52, affordable ? GOLD : 0x2a2a3a)
      .setStrokeStyle(2, affordable ? 0xfff3b0 : 0x555a66)
      .setInteractive({ useHandCursor: affordable })
      .setDepth(2);
    container.add(btn);
    container.add(
      this.add
        .text(right - 105, y, 'BUY', {
          fontSize: '20px',
          color: affordable ? '#1a140a' : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(3),
    );
    if (affordable) {
      btn.on('pointerover', () => btn.setFillStyle(0xffd83a));
      btn.on('pointerout', () => btn.setFillStyle(GOLD));
    }
    btn.on('pointerup', () => {
      if (this.confirmOpen) return;
      if (affordable) this.showConfirm(type, price);
      else this.flashHint('Not enough coins — win episodes to earn more.');
    });
  }

  /** Transient footer hint (e.g. can't afford). */
  private flashHint(msg: string): void {
    this.hint?.destroy();
    this.hint = this.add
      .text(640, 672, msg, { fontSize: '15px', color: '#e8a0a0', fontStyle: 'italic' })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(1800, () => {
      this.hint?.destroy();
      this.hint = undefined;
    });
  }

  /** Purchase confirm — the LevelSelect reset-modal pattern, gold-trimmed. */
  private showConfirm(type: ShopTowerType, price: number): void {
    const W = 1280;
    const H = 720;
    const D = 100;
    this.confirmOpen = true;
    const objects: Phaser.GameObjects.GameObject[] = [];

    objects.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    objects.push(
      this.add
        .rectangle(W / 2, H / 2, 560, 260, 0x1a1726, 1)
        .setStrokeStyle(2, GOLD)
        .setDepth(D + 1),
    );
    objects.push(
      this.add
        .text(W / 2, H / 2 - 76, `Buy ${TOWER_NAMES[type]}?`, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objects.push(
      this.add
        .text(
          W / 2,
          H / 2 - 22,
          `${price} Hambi coins · one-time purchase.\nOnce owned, available in every episode.`,
          { fontSize: '16px', color: '#9aa6ad', align: 'center', lineSpacing: 4 },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const teardown = () => {
      objects.forEach((o) => o.destroy());
      this.confirmOpen = false;
    };

    // Confirm (green) — at (520, 430) for the e2e click.
    const confirmBg = this.add
      .rectangle(W / 2 - 120, H / 2 + 70, 200, 52, 0x1a6b1a)
      .setStrokeStyle(2, 0x4caf50)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(confirmBg);
    objects.push(
      this.add
        .text(W / 2 - 120, H / 2 + 70, 'BUY', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    confirmBg.on('pointerup', () => {
      const ok = this.progress.purchaseTower(type, price);
      teardown();
      if (ok) {
        recordStat('obtains'); // Quests: a new tower
        // Shopper: first tower ever bought from the Shop (award is idempotent).
        try {
          const a = new RankService(saveStorage()).award('shopper');
          if (a) enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
        } catch (err) {
          console.warn('[ShopScene] achievement award failed', err);
        }
        // State sweep: Be Wise / Shoplifter etc. may have just been satisfied.
        sweepAndToastAchievements(saveStorage());
        this.balanceText.setText(coinLabel(this.progress.coins()));
        this.renderRows();
      }
    });

    // Cancel — at (760, 430).
    const cancelBg = this.add
      .rectangle(W / 2 + 120, H / 2 + 70, 200, 52, 0x2a2a3a)
      .setStrokeStyle(2, 0x6666aa)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(cancelBg);
    objects.push(
      this.add
        .text(W / 2 + 120, H / 2 + 70, 'CANCEL', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    cancelBg.on('pointerup', teardown);
  }
}
